import React, { type FC, type ReactNode, memo } from "react";
import { cn } from "@heroui/react";

import styles from "./AgentMessageView.module.css";
import type { AgentVisualState } from "./runner/types";
import { useTranslation } from "@domain/intl/useTranslation";

const shapes: Record<AgentVisualState, ReactNode> = {
    reading: (
        <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeDasharray="2.5 3" />
    ),
    asking: (
        <>
            <circle cx="10" cy="10" r="9" fill="currentColor" />
            <path
                d="M7.6 7.8a2.5 2.5 0 114 2c-.9.6-1.6 1-1.6 2.1"
                fill="none"
                stroke="#fff"
                strokeWidth="1.6"
                strokeLinecap="round"
            />
            <circle cx="10" cy="14.3" r=".9" fill="#fff" />
        </>
    ),
    running: (
        <>
            <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeOpacity=".2" strokeWidth="2.2" />
            <path d="M10 2a8 8 0 018 8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </>
    ),
    pausing: (
        <>
            <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeOpacity=".2" strokeWidth="2.2" />
            <path d="M10 2a8 8 0 018 8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </>
    ),
    paused: (
        <>
            <circle cx="10" cy="10" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M8 6.8v6.4M12 6.8v6.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </>
    ),
    done: (
        <>
            <circle cx="10" cy="10" r="9" fill="currentColor" />
            <path
                d="M6 10.3l2.7 2.7L14 7.5"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </>
    ),
    nothing: (
        <>
            <circle cx="10" cy="10" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M6.5 10h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </>
    ),
    skipped: (
        <>
            <circle cx="10" cy="10" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path
                d="M7.5 6.5l4 3.5-4 3.5M13 6.5v7"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </>
    ),
    failed: (
        <>
            <path
                d="M10 1.8l8.5 15.2h-17z"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
            />
            <path d="M10 7.5v4.2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="10" cy="14.2" r="1" fill="#fff" />
        </>
    ),
    pending: <circle cx="10" cy="10" r="3.2" fill="currentColor" />,
};

const colors: Record<AgentVisualState, string> = {
    reading: "text-muted",
    asking: "text-accent",
    running: "text-accent",
    pausing: "text-warning",
    paused: "text-warning",
    done: "text-success",
    nothing: "text-muted",
    skipped: "text-muted",
    failed: "text-danger",
    pending: "text-separator",
};

interface Props {
    state: AgentVisualState;
    className?: string;
}

const AgentStateIconImpl: FC<Props> = ({ state, className }) => {
    const { t } = useTranslation();
    return (
        <span
            data-testid={`agent-state-${state}`}
            className={cn("inline-flex shrink-0 align-middle", colors[state], className)}
        >
            <svg
                aria-hidden
                viewBox="0 0 20 20"
                className={cn("size-5", (state === "running" || state === "pausing") && styles.spin)}
            >
                {shapes[state]}
            </svg>
            <span className="sr-only">{t(`app.agent.state.${state}`)}</span>
        </span>
    );
};

export const AgentStateIcon = memo(AgentStateIconImpl);
